import React, { useEffect, useState, useCallback } from "react";
import { Lock, ShieldAlert, KeyRound, Loader2 } from "lucide-react";
import Phonebook from "@/components/Phonebook";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useFeatureFlags, FeatureDisabled } from "@/lib/featureFlags";
import { useReference } from "@/lib/useReference";
import { useAuth } from "@/context/AuthContext";

const SS_KEY = "phonebook_unlock_token";

function GateShell({ children }) {
  return (
    <div>
      <PageHeader title="Phonebook" subtitle="Protected by your personal PIN." />
      <div className="mx-auto max-w-md">{children}</div>
    </div>
  );
}

export default function CoachPhonebook() {
  const { phonebook: phonebookOn } = useFeatureFlags();
  const { ref } = useReference();
  const { user } = useAuth();
  const [token, setToken] = useState(() => sessionStorage.getItem(SS_KEY) || "");
  const [status, setStatus] = useState(null); // { pin_set, locked }
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/phonebook/status");
      setStatus(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  useEffect(() => { if (!token) loadStatus(); }, [token, loadStatus]);

  const unlock = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/coach/phonebook/unlock", { pin });
      sessionStorage.setItem(SS_KEY, data.unlock_token);
      setToken(data.unlock_token);
      setPin("");
    } catch (err) {
      const code = err.response?.status;
      toast.error(formatApiError(err.response?.data?.detail));
      if (code === 423) loadStatus(); // now locked
      setPin("");
    } finally { setBusy(false); }
  };

  const handleLocked = useCallback((code) => {
    sessionStorage.removeItem(SS_KEY);
    setToken("");
    if (code === 423) toast.error("Your phonebook is locked. Ask an administrator to unlock it.");
    else toast.info("Your phonebook session expired. Please re-enter your PIN.");
    loadStatus();
  }, [loadStatus]);

  // Unlocked for this session → render the full (unmasked) phonebook.
  if (!phonebookOn) return <FeatureDisabled name="Phonebook" />;
  if (token) {
    return (
      <Phonebook
        endpoint="/coach/contacts"
        requestHeaders={{ "X-Phonebook-Token": token }}
        onLocked={handleLocked}
        deptDistricts={ref.districts}
        defaultDeptDistrict={user?.district_id || ""}
        title="Phonebook"
        subtitle="Your contacts, sorted A–Z. System Administrator contacts are pinned on top."
      />
    );
  }

  if (!status) {
    return <GateShell><div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></GateShell>;
  }

  if (status.locked) {
    return (
      <GateShell>
        <Card className="border-red-200 bg-red-50/50 p-8 text-center shadow-sm" data-testid="phonebook-locked">
          <ShieldAlert className="mx-auto h-10 w-10 text-red-500" />
          <h3 className="mt-4 text-lg font-semibold text-slate-800 font-heading">Phonebook locked</h3>
          <p className="mt-2 text-sm text-slate-600">Too many incorrect PIN attempts. Only an administrator can unlock your phonebook. Please contact your administrator.</p>
        </Card>
      </GateShell>
    );
  }

  if (!status.pin_set) {
    return (
      <GateShell>
        <Card className="p-8 text-center shadow-sm" data-testid="phonebook-no-pin">
          <KeyRound className="mx-auto h-10 w-10 text-sky-500" />
          <h3 className="mt-4 text-lg font-semibold text-slate-800 font-heading">Set up your phonebook PIN</h3>
          <p className="mt-2 text-sm text-slate-600">The phonebook is protected by a personal 4 or 6-digit PIN. Set one up in your profile to continue.</p>
          <Button asChild className="mt-5 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="phonebook-setup-link">
            <Link to="/coach/profile">Set up my PIN</Link>
          </Button>
        </Card>
      </GateShell>
    );
  }

  return (
    <GateShell>
      <Card className="p-8 shadow-sm" data-testid="phonebook-pin-gate">
        <div className="text-center">
          <Lock className="mx-auto h-10 w-10 text-slate-400" />
          <h3 className="mt-4 text-lg font-semibold text-slate-800 font-heading">Enter your PIN</h3>
          <p className="mt-2 text-sm text-slate-500">Your phonebook is protected. Enter your PIN to unlock it for this session.</p>
        </div>
        <form onSubmit={unlock} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label>PIN</Label>
            <Input
              type="password" inputMode="numeric" autoFocus autoComplete="off"
              maxLength={6} placeholder="••••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              className="text-center tracking-[0.5em]"
              data-testid="phonebook-pin-input"
            />
          </div>
          <Button type="submit" disabled={busy || !(pin.length === 4 || pin.length === 6)} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="phonebook-pin-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Unlock phonebook
          </Button>
        </form>
      </Card>
    </GateShell>
  );
}
